import { track } from "@/lib/analytics";
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sun, CheckCircle, ArrowRight, FileText, Shield, Zap, Users, ChevronDown, Gauge, Box, Receipt, Wallet, X, Check } from "lucide-react";
import { PLAN_PRICING, fmtINR, limitLabel, SALES_EMAIL } from "@/lib/plans";

const OA_STATES = [
  { label: "Maharashtra", path: "/OA-Assessment-MH" },
];

const FIELD_FEATURES = [
  "Daily progress reports with photos, from the site",
  "Task status and material receipts (MRN) from a phone",
  "Document vault and client portal updated from one entry",
  "Works on any phone — no app to install",
];

const FLOW = [
  ["Bill audit",      "Upload the DISCOM bill. Tariff checks, PF and demand findings, and a policy-correct plant size — rooftop and open access — with every rule cited."],
  ["3D design",       "Roofs, obstacles, shadows, panel layout and a 3D impression — exported straight into the proposal."],
  ["Proposal",        "Branded, bankable proposal with savings slot by slot against the real ToD tariff, in minutes."],
  ["Bank DPR",        "Detailed project report for the lender: IRR, DSCR, payback, annexures — generated, not typed."],
  ["Project",         "Lead converts to a project with 63–87 tasks across 8 phases, Gantt, Kanban, site DPRs from the field."],
  ["Procure & bill",  "GST-ready POs, PIs, MRNs, item catalogue, milestone billing tied to commissioning, receivables ageing."],
  ["Get paid",        "Payments ledger, client portal with documents and invoices, automatic reminders. Cash, not chaos."],
];

const FEATURES = [
  { icon: Gauge,     title: "Bill audit & sizing",      desc: "Reads DHBVN, UHBVN, MSEDCL, GUVNL bills. Finds billing errors and PF/CD savings. Sizes rooftop and open-access plants hour by hour under the state's ToD, banking, net-metering caps and storage mandates." },
  { icon: Box,       title: "3D design & layout",       desc: "Draw roofs and obstacles, set pitch and azimuth, place tables with inter-row shadows, and export a 3D impression into the proposal." },
  { icon: FileText,  title: "Proposals & bank DPRs",    desc: "Branded proposals with savings against the real tariff, and bankable DPRs with IRR, DSCR and payback for the lender — from the same inputs." },
  { icon: Zap,       title: "Projects, 8 phases",        desc: "63 to 87 tasks auto-generated per project type, re-planned when the commissioning date moves. Gantt, Kanban, RAG status, site DPRs from mobile." },
  { icon: Receipt,   title: "Procurement & GST documents", desc: "Purchase orders, proforma invoices and material receipts with line-item GST, atomic numbering, an item catalogue that learns from every order." },
  { icon: Wallet,    title: "Milestone billing & receivables", desc: "Bill against delivery and commissioning milestones, retention tied to COD, payment ledger, ageing, TDS and automated client reminders." },
  { icon: Users,     title: "Roles that match an EPC",  desc: "Owner, PM, engineer, sales, procurement, accountant, field, client — each sees exactly their work and money, nothing else." },
  { icon: Shield,    title: "Isolated workspaces",      desc: "Every company in its own tenant, capability-based permissions, audit of who changed what. Built for a team, priced for a small one." },
];

const RULES = [
  ["Haryana",     "HERC ToD, 30% monthly banking at 8% in kind, 500 kW net-metering cap, OA capped at contract demand, no storage mandate"],
  ["Maharashtra", "MSEDCL ToD with solar-hour rebate and evening peak, rule-C slot-locked banking, 5 MW net metering, 1 MWh/MW storage mandate from April 2026"],
  ["Gujarat",     "GERC ToU, cash banking charge, 1,000 kW net metering, third-party OA held to contract demand"],
  ["Next",        "Rajasthan, Uttar Pradesh, Punjab — same method, same sourcing"],
];

const NOT_GENERIC = [
  ["Size a plant from a DISCOM bill under the state's ToD and banking rules", true,  false],
  ["Apply net-metering caps and storage mandates, with the order cited",     true,  false],
  ["Proposal and bank DPR from the same inputs",                              true,  false],
  ["Project plan that re-schedules when the COD moves",                       true,  false],
  ["GST POs / PIs / MRNs with milestone billing and retention",               true,  false],
  ["Generic task boards and spreadsheets",                                    true,  true ],
];

const PLANS = PLAN_PRICING.map(p => ({
  name: p.name, price: fmtINR(p.price), period: "/month", annual: `or ${fmtINR(p.annual)}/year`,
  projects: p.projects === -1 ? "Unlimited projects" : `${p.projects} projects running at once`,
  users: limitLabel(p.users, "users"), storage: p.storage_gb === -1 ? "Unlimited storage" : `${p.storage_gb} GB file storage`, highlight: p.highlight,
}));

const STATS = [
  { n: "10 min",   label: "Bill to bankable proposal" },
  { n: "3 states", label: "Regulations modelled, sourced" },
  { n: "8 phases", label: "63–87 tasks per project" },
  { n: "1 tool",   label: "Sales, design, finance, site" },
];

export default function LandingPage() {
  const [oaOpen, setOaOpen] = useState(false);
  const oaRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (oaRef.current && !oaRef.current.contains(e.target)) setOaOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div style={{ fontFamily: "Manrope, sans-serif", background: "#0B0B0F", color: "#E8ECF0", minHeight: "100vh" }}>

      {/* ── NAV ──────────────────────────────────────────────────────────── */}
      <nav
        style={{
          position: "sticky", top: 0, zIndex: 50,
          background: "rgba(11,11,15,0.85)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px", height: "64px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "#FFB703", boxShadow: "0 0 20px rgba(255,183,3,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Sun size={15} color="#0A1128" />
            </div>
            <div>
              <div style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "16px", color: "white", letterSpacing: "-0.02em", lineHeight: 1 }}>SunBiz</div>
              <div style={{ fontSize: "9px", fontWeight: "600", letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", lineHeight: 1, marginTop: "3px" }}>by Sun Bizcon</div>
            </div>
          </div>

          <div className="hidden md:flex" style={{ gap: "32px", alignItems: "center" }}>
            {["Features", "Regulations", "Pricing"].map(item => (
              <a key={item} href={`#${item.toLowerCase()}`}
                style={{ color: "rgba(255,255,255,0.4)", fontSize: "14px", textDecoration: "none", transition: "color 0.2s ease" }}
                onMouseEnter={e => { e.currentTarget.style.color = "rgba(255,255,255,0.85)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}
              >{item}</a>
            ))}

            {/* OA Assessment Dropdown */}
            <div ref={oaRef} style={{ position: "relative" }}>
              <button
                onClick={() => setOaOpen(p => !p)}
                data-testid="oa-assessment-dropdown"
                style={{
                  color: oaOpen ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.4)",
                  fontSize: "14px", background: "none", border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "4px", padding: 0,
                  transition: "color 0.2s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "rgba(255,255,255,0.85)"; }}
                onMouseLeave={e => { if (!oaOpen) e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}
              >
                OA Assessment
                <ChevronDown size={14} style={{ transform: oaOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }} />
              </button>
              {oaOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 10px)", left: "50%", transform: "translateX(-50%)",
                  background: "#1A1A22", border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "10px", padding: "6px", minWidth: "200px",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
                  animation: "fadeIn 0.15s ease",
                }}>
                  {OA_STATES.map(st => (
                    <Link key={st.path} to={st.path}
                      onClick={() => setOaOpen(false)}
                      data-testid={`oa-link-${st.path.slice(1)}`}
                      style={{
                        display: "block", padding: "9px 14px", borderRadius: "7px",
                        color: "rgba(255,255,255,0.7)", fontSize: "13px", fontWeight: "500",
                        textDecoration: "none", transition: "background 0.15s ease, color 0.15s ease",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = "#fff"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
                    >{st.label}</Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Link to="/login" className="lp-nav-login"
              style={{ color: "rgba(255,255,255,0.45)", fontSize: "14px", fontWeight: "500", padding: "8px 16px", textDecoration: "none", transition: "color 0.2s ease" }}
              onMouseEnter={e => { e.currentTarget.style.color = "rgba(255,255,255,0.8)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.45)"; }}
            >Sign In</Link>
            <Link to="/register" className="lp-nav-cta"
              data-testid="get-started-btn"
              style={{
                background: "#FFB703", color: "#0A1128", fontSize: "13px", fontWeight: "800",
                padding: "9px 18px", borderRadius: "100px", textDecoration: "none",
                boxShadow: "0 0 18px rgba(255,183,3,0.3)", transition: "box-shadow 0.2s ease, transform 0.15s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 28px rgba(255,183,3,0.5)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 18px rgba(255,183,3,0.3)"; e.currentTarget.style.transform = "translateY(0)"; }}
            >Start free trial</Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <style>{`
        @media (max-width: 480px) {
          .lp-hero { padding: 44px 0 56px !important; }
          .lp-hero h1 { font-size: 36px !important; margin-bottom: 16px !important; }
          .lp-hero-sub { font-size: 15px !important; line-height: 1.55 !important; margin-bottom: 22px !important; }
          .lp-nav-login { padding: 8px 8px !important; white-space: nowrap; }
          .lp-nav-cta { white-space: nowrap; padding: 8px 14px !important; }
          .lp-hero-cta { width: 100%; justify-content: center; }
        }
      `}</style>
      <section className="lp-hero" style={{ position: "relative", overflow: "hidden", padding: "100px 0 80px" }}>
        {/* Mesh background */}
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 25% 40%, rgba(255,183,3,0.12) 0%, transparent 55%), radial-gradient(ellipse at 75% 60%, rgba(34,211,238,0.10) 0%, transparent 55%), radial-gradient(ellipse at 50% 90%, rgba(34,211,238,0.08) 0%, transparent 50%)", zIndex: 0 }} />
        {/* Solar image overlay */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url('https://images.unsplash.com/photo-1590022560680-ebd6b24a6de4?crop=entropy&cs=srgb&fm=jpg&q=60&w=1600')`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.07, zIndex: 0 }} />
        {/* Grid */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "60px 60px", opacity: 0.025, zIndex: 0 }} />

        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px", position: "relative", zIndex: 1 }}>
          <div style={{ maxWidth: "700px" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "rgba(255,183,3,0.1)", border: "1px solid rgba(255,183,3,0.3)", color: "#FFB703", fontSize: "11px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", padding: "6px 14px", borderRadius: "100px", marginBottom: "28px" }}>
              <Zap size={11} />
              The operating system for Indian solar EPCs
            </div>
            <h1 style={{ fontFamily: "Outfit", fontWeight: "800", fontSize: "clamp(40px, 6vw, 66px)", letterSpacing: "-0.04em", lineHeight: "1.08", color: "white", marginBottom: "24px" }}>
              From the customer's{" "}
              <span style={{ background: "linear-gradient(135deg, #FFB703, #22D3EE)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>electricity bill</span>
              <br />to collected cash.
            </h1>
            <p className="lp-hero-sub" style={{ fontSize: "18px", lineHeight: "1.7", color: "rgba(255,255,255,0.5)", marginBottom: "36px", maxWidth: "560px" }}>
              Upload a DISCOM bill and walk out with a policy-correct plant size and a bankable proposal in ten minutes. Then run the design, the bank DPR, the project, procurement, GST billing and collections in the same place — the way an Indian EPC actually works.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
              <Link to="/register" className="lp-hero-cta"
                data-testid="hero-start-btn"
                style={{
                  background: "#FFB703", color: "#0A1128", fontSize: "15px", fontWeight: "800", padding: "14px 28px", borderRadius: "100px", textDecoration: "none",
                  display: "inline-flex", alignItems: "center", gap: "10px",
                  boxShadow: "0 0 28px rgba(255,183,3,0.35)", transition: "box-shadow 0.2s ease, transform 0.15s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 44px rgba(255,183,3,0.55)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 28px rgba(255,183,3,0.35)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                Audit a bill free <ArrowRight size={17} />
              </Link>
              <a href={`mailto:${SALES_EMAIL}?subject=SunBiz%20demo`} onClick={() => track("generate_lead", { source: "mailto" })}
                data-testid="hero-demo-btn"
                style={{
                  color: "rgba(255,255,255,0.6)", fontSize: "14px", fontWeight: "600", padding: "13px 24px", borderRadius: "100px",
                  border: "1px solid rgba(255,255,255,0.14)", textDecoration: "none", transition: "all 0.2s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "rgba(255,255,255,0.9)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}
              >Book a 15-minute demo</a>
            </div>
            <p style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)", marginTop: "16px" }}>
              14 days free · No credit card · Built by solar professionals with 150+ MWp delivered across rooftop and open access
            </p>
          </div>
          <div style={{ marginTop: "56px", borderRadius: "18px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,183,3,0.08)", background: "#fff" }} data-testid="hero-shot">
            <img src="/screens/rooftop-audit.jpg" alt="SunBiz bill audit — rooftop sizing for a Maharashtra HT consumer with the storage mandate applied, hour-by-hour dispatch and the regulations cited" style={{ width: "100%", display: "block" }} loading="eager" />
          </div>
          <p style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)", marginTop: "12px", textAlign: "center" }}>
            A real audit: a 1,050 kVA Maharashtra HT consumer sized to the net-metering cap, the April 2026 storage mandate applied, every rule cited.
          </p>
        </div>
      </section>

      {/* ── STATS BAR ─────────────────────────────────────────────────────── */}
      <section style={{ background: "rgba(255,255,255,0.025)", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "32px 0" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "24px", textAlign: "center" }}>
          {STATS.map((s) => (
            <div key={s.label}>
              <div style={{ fontFamily: "Outfit", fontWeight: "800", fontSize: "32px", letterSpacing: "-0.04em", background: "linear-gradient(135deg, #FFB703, #22D3EE)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{s.n}</div>
              <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.35)", marginTop: "4px", letterSpacing: "0.04em" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ──────────────────────────────────────────────────────── */}
      <section id="features" style={{ padding: "96px 0" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>
          <div style={{ textAlign: "center", marginBottom: "56px" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase", color: "#FFB703", marginBottom: "14px" }}>One flow, one tool</div>
            <h2 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "clamp(28px, 4vw, 40px)", letterSpacing: "-0.03em", color: "white", marginBottom: "12px" }}>
              Everything between the bill and the bank
            </h2>
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "16px", maxWidth: "560px", margin: "0 auto" }}>
              Sales, engineering, finance and site in one system of record — so the proposal, the DPR, the schedule and the invoice never disagree.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px", marginBottom: "48px" }} data-testid="flow-strip">
            {FLOW.map(([t, d], i) => (
              <div key={t} style={{ background: "#16161D", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "14px", padding: "14px 14px 16px", position: "relative" }}>
                <div style={{ fontSize: "10px", fontWeight: "800", letterSpacing: "0.1em", textTransform: "uppercase", color: "#FFB703", marginBottom: "6px" }}>{i + 1}</div>
                <div style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "14px", color: "white", marginBottom: "6px" }}>{t}</div>
                <div style={{ fontSize: "11.5px", color: "rgba(255,255,255,0.45)", lineHeight: 1.55 }}>{d}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginBottom: "32px" }}>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              const colors = ["#FFB703", "#22D3EE", "#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#34D399"];
              const c = colors[i % colors.length];
              return (
                <div
                  key={f.title}
                  style={{
                    background: "#16161D", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "16px",
                    padding: "24px", transition: "transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.borderColor = `${c}40`; e.currentTarget.style.boxShadow = `0 8px 32px ${c}20`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "rgba(46,100,68,0.1)"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: `${c}18`, border: `1px solid ${c}30`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                    <Icon size={18} style={{ color: c }} />
                  </div>
                  <h3 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "15px", color: "white", marginBottom: "8px", letterSpacing: "-0.02em" }}>{f.title}</h3>
                  <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.4)", lineHeight: "1.65" }}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── REGULATIONS ───────────────────────────────────────────────────── */}
      <section id="regulations" style={{ padding: "80px 0", background: "rgba(255,255,255,0.02)", borderTop: "1px solid rgba(46,100,68,0.08)", borderBottom: "1px solid rgba(46,100,68,0.08)" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase", color: "#FFB703", marginBottom: "14px" }}>Built on the regulations, not around them</div>
            <h2 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "clamp(26px, 3.5vw, 38px)", letterSpacing: "-0.03em", color: "white", marginBottom: "12px" }}>
              Every number carries its order, and how sure we are of it
            </h2>
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "15px", maxWidth: "620px", margin: "0 auto" }}>
              ToD slots, banking rules, net-metering caps, open-access charges and storage mandates change every year. SunBiz models them state by state, cites the regulation on the page and in the report, and marks each figure CONFIRMED, PARTIAL or UNVERIFIED — so your proposal survives the DISCOM and the bank.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "14px", marginBottom: "48px" }} data-testid="rules-grid">
            {RULES.map(([st, d]) => (
              <div key={st} style={{ background: "#16161D", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "12px", padding: "16px 18px" }}>
                <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: st === "Next" ? "rgba(255,255,255,0.35)" : "#FFB703", marginBottom: "6px" }}>{st === "Next" ? "Coming next" : st}</div>
                <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.6)", lineHeight: 1.6 }}>{d}</div>
              </div>
            ))}
          </div>
          <div style={{ maxWidth: "820px", margin: "0 auto", background: "#16161D", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", overflow: "hidden" }} data-testid="compare-table">
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 72px 96px", padding: "12px 14px", fontSize: "10.5px", fontWeight: "800", letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <span>What an EPC needs</span><span style={{ textAlign: "center", color: "#FFB703" }}>SunBiz</span><span style={{ textAlign: "center" }}>Generic tools</span>
            </div>
            {NOT_GENERIC.map(([t, a, b]) => (
              <div key={t} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 72px 96px", padding: "11px 14px", fontSize: "13px", color: "rgba(255,255,255,0.75)", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center" }}>
                <span>{t}</span>
                <span style={{ textAlign: "center" }}>{a ? <Check size={16} style={{ color: "#34D399" }} /> : <X size={16} style={{ color: "#F87171" }} />}</span>
                <span style={{ textAlign: "center" }}>{b ? <Check size={16} style={{ color: "#34D399" }} /> : <X size={16} style={{ color: "#F87171" }} />}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FIELD TEAM ────────────────────────────────────────────────────── */}
      <section style={{ padding: "96px 0" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "56px" }}>
          <div style={{ flex: "1", minWidth: "280px" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase", color: "#FFB703", marginBottom: "18px" }}>Mobile-first for field teams</div>
            <h2 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "clamp(26px, 3.5vw, 38px)", letterSpacing: "-0.03em", color: "white", marginBottom: "18px", lineHeight: "1.15" }}>
              Your engineers work in the field.<br />So does SunBiz.
            </h2>
            <p style={{ color: "rgba(255,255,255,0.4)", lineHeight: "1.7", marginBottom: "28px", fontSize: "15px" }}>
              Field engineers log daily progress, update tasks, upload site photos and material receipts from their phone. The owner sees the schedule move, the accountant sees the milestone come due, the client sees the portal update — from one entry.
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
              {FIELD_FEATURES.map((item) => (
                <li key={item} style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", color: "rgba(255,255,255,0.55)" }}>
                  <div style={{ width: "18px", height: "18px", borderRadius: "50%", background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#34D399" }} />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div style={{ flex: "1", minWidth: "280px", borderRadius: "20px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <img
              src="https://images.unsplash.com/photo-1659353587228-5dbbebb5d98f?crop=entropy&cs=srgb&fm=jpg&q=80&w=640"
              alt="Field engineer with tablet"
              style={{ width: "100%", height: "340px", objectFit: "cover", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────────────────────── */}
      <section id="pricing" style={{ padding: "96px 0", background: "rgba(255,255,255,0.02)", borderTop: "1px solid rgba(46,100,68,0.08)" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>
          <div style={{ textAlign: "center", marginBottom: "56px" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase", color: "#FFB703", marginBottom: "14px" }}>Pricing</div>
            <h2 style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "clamp(26px, 3.5vw, 38px)", letterSpacing: "-0.03em", color: "white", marginBottom: "12px" }}>
              Simple, transparent pricing
            </h2>
            <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "15px" }}>Every plan includes the bill audit, design, proposals, DPRs, projects, procurement, billing and the client portal. 14-day free trial, no credit card.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px", maxWidth: "900px", margin: "0 auto" }}>
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                style={{
                  background: plan.highlight ? "rgba(255,183,3,0.07)" : "#16161D",
                  border: plan.highlight ? "1px solid rgba(255,183,3,0.45)" : "1px solid rgba(46,100,68,0.1)",
                  borderRadius: "20px", padding: "28px",
                  boxShadow: plan.highlight ? "0 0 40px rgba(255,183,3,0.12), 0 0 0 1px rgba(255,183,3,0.15) inset" : "none",
                  position: "relative", transition: "transform 0.25s ease",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}
              >
                {plan.highlight && (
                  <div style={{ position: "absolute", top: "-12px", left: "50%", transform: "translateX(-50%)", background: "#FFB703", color: "#0A1128", fontSize: "10px", fontWeight: "800", letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 14px", borderRadius: "100px", whiteSpace: "nowrap", boxShadow: "0 0 16px rgba(255,183,3,0.4)" }}>
                    Most Popular
                  </div>
                )}
                <div style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "18px", color: "white", marginBottom: "8px" }}>{plan.name}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: "4px", marginBottom: "4px" }}>
                  <span style={{ fontFamily: "Outfit", fontWeight: "800", fontSize: "34px", letterSpacing: "-0.04em", color: plan.highlight ? "#FFB703" : "white" }}>{plan.price}</span>
                  <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.3)" }}>{plan.period}</span>
                </div>
                <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.3)", marginBottom: "20px" }}>{plan.annual} · + 18% GST</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "28px" }}>
                  {[plan.projects, plan.users, plan.storage, "All features included"].map((item, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", color: "rgba(255,255,255,0.55)" }}>
                      <CheckCircle size={14} style={{ color: "#34D399", flexShrink: 0 }} />
                      {item}
                    </div>
                  ))}
                </div>
                <Link
                  to="/register"
                  style={{
                    display: "block", textAlign: "center", padding: "12px 0", borderRadius: "12px", fontWeight: "700", fontSize: "14px", textDecoration: "none",
                    background: plan.highlight ? "#FFB703" : "rgba(255,255,255,0.06)",
                    color: plan.highlight ? "#0A1128" : "rgba(255,255,255,0.6)",
                    border: plan.highlight ? "none" : "1px solid rgba(255,255,255,0.1)",
                    boxShadow: plan.highlight ? "0 0 20px rgba(255,183,3,0.3)" : "none",
                    transition: "opacity 0.2s ease",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = "0.85"; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                >
                  Start Free Trial
                </Link>
              </div>
            ))}
          </div>
          <p data-testid="pricing-note" style={{ textAlign: "center", marginTop: "28px", fontSize: "13px", color: "rgba(255,255,255,0.4)", maxWidth: "640px", marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>
            Projects running at once counts only live projects. Finished projects stay in your account for free.
          </p>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section style={{ padding: "96px 0" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
          <div style={{ background: "linear-gradient(135deg, rgba(255,183,3,0.10), rgba(34,211,238,0.06))", border: "1px solid rgba(255,183,3,0.2)", borderRadius: "24px", padding: "64px 40px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(255,183,3,0.16) 0%, transparent 60%)", pointerEvents: "none" }} />
            <h2 style={{ fontFamily: "Outfit", fontWeight: "800", fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.04em", color: "white", marginBottom: "16px", position: "relative" }}>
              Bring one real bill. Leave with a proposal.
            </h2>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "16px", marginBottom: "36px", maxWidth: "520px", margin: "0 auto 36px", position: "relative" }}>
              Fifteen minutes with your own customer's electricity bill is the fastest way to see what SunBiz does. No migration, no setup — the rest of the suite is there when you want it.
            </p>
            <Link to="/register"
              style={{
                background: "#FFB703", color: "#0A1128", fontSize: "15px", fontWeight: "800",
                padding: "14px 32px", borderRadius: "100px", textDecoration: "none",
                display: "inline-flex", alignItems: "center", gap: "10px", position: "relative",
                boxShadow: "0 0 32px rgba(255,183,3,0.4)", transition: "box-shadow 0.2s ease, transform 0.15s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 52px rgba(255,183,3,0.6)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 32px rgba(255,183,3,0.4)"; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              Start the 14-day free trial <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "40px 0" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "28px", height: "28px", borderRadius: "8px", background: "#FFB703", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Sun size={13} color="#0A1128" />
            </div>
            <div>
              <div style={{ fontFamily: "Outfit", fontWeight: "700", fontSize: "14px", color: "white", lineHeight: 1 }}>SunBiz</div>
              <div style={{ fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.2)", lineHeight: 1, marginTop: "3px" }}>by Sun Bizcon</div>
            </div>
          </div>
          <p style={{ fontSize: "12px", color: "rgba(255,255,255,0.2)" }}>© {new Date().getFullYear()} SunBiz by Sun Bizcon · Gurugram · <a href={`mailto:${SALES_EMAIL}`} style={{ color: "rgba(255,255,255,0.35)", textDecoration: "none" }}>{SALES_EMAIL}</a> · <Link to="/solar-project-management-software-india" style={{ color: "rgba(255,255,255,0.35)", textDecoration: "none" }}>Solar project management software in India</Link></p>
          <div style={{ display: "flex", gap: "24px" }}>
            {[["Sign In", "/login"], ["Register", "/register"]].map(([label, path]) => (
              <Link key={path} to={path} style={{ fontSize: "12px", color: "rgba(255,255,255,0.25)", textDecoration: "none", transition: "color 0.2s ease" }}
                onMouseEnter={e => { e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.25)"; }}
              >{label}</Link>
            ))}
          </div>
        </div>
      </footer>

    </div>
  );
}
